import { useEffect, useState } from 'react'
import { Input } from '@panwds/react-form'
import { useFormContext } from 'react-hook-form'
import type { VFC } from 'react'
import { Title } from '@panwds/react-ui'
import { isNonemptyArray } from './common/utils'
import SubscriptionService from '../services/SubscriptionService'
import type { CommonStoreState, ContextApp } from '../store'
import { REGION_FIELD_NAME, TSG_FIELD_NAME } from './common/GlobalConstants'
import { URL_VALIDATIONS, URL_VALIDATIONS_REGEX, CONTEXT_TO_APP_ID, APP_SUBDOMAIN } from '../constants'

type DefaultSubDomainFieldProps = {
  state: CommonStoreState
}

type UrlRegexValidation = {
  [APP_ID_IOT: string]: any
}

const ContextToAppId: ContextApp = CONTEXT_TO_APP_ID
const urlValidationsRegex: UrlRegexValidation = URL_VALIDATIONS_REGEX

export const Validation = ({ ...params }) => {
  const { appId, domainPostFix, tsgId, validationRules, subExistingDomain } = params
  // for create_new tsg selection, there is no tsg_id hence fallback to globalTsgId value to validate subdomain API, incase of day 0 globalTsgID will be 'none'
  const tsg_id = tsgId
  const validationRequired = validationRules.validation_required
  let isFetching = false
  return {
    urlValidation: async (value: any) => {
      const lowerValue = value.toLowerCase()
      if (URL_VALIDATIONS.includes(appId) && validationRequired && !subExistingDomain) {
        const regexExpression = urlValidationsRegex[appId]
        if (value === '' || value?.trim().length === 0) {
          return 'Please enter a valid subdomain'
        }
        if (!regexExpression.test(value)) {
          return 'Entered subdomain is not valid'
        }
        else if (!isFetching) { // to avoid duplicate calls tracking with isFetching
          const request = {
            'subdomain': lowerValue,
            'domainPostfix': domainPostFix
          }
          isFetching = true
          const validationResponse: any = await SubscriptionService.validateSubDomain.post(request, tsg_id, appId).catch(error => {
            return { error: error.message }
          })
          if (validationResponse?.error) {
            return validationResponse.error
          }
          else if (!validationResponse) {
            return 'The domain already in use'
          }
          isFetching = false //reset
          return true
        }
      }
      return true
    }
  }
}

const SubDomainField: VFC<DefaultSubDomainFieldProps> = ({ state }) => {
  const { context, instances, tsg, apps, globalTsgId } = state
  const tsgId = tsg?.tsg_id || globalTsgId
  const [contextAppId, setContextAppId] = useState<string>()
  const [subExistingDomain, setSubExistingDomain] = useState<string|undefined>()
  const [domainPostFix, setDomainPostFix] = useState<string>('')
  const [currentTypedInSubDomain, setCurrentTypedInSubDomain] = useState<string|undefined>()

  const { getValues, setValue, clearErrors } = useFormContext()
  const region = getValues(REGION_FIELD_NAME)
  const dpTsgId = getValues(TSG_FIELD_NAME)?.id

  useEffect(() => {
    if (context) {
      setContextAppId(ContextToAppId[context])
    }
  }, [context])

  useEffect(() => {
    if (contextAppId) {
      const appDef = apps.find(e => e.app_id === contextAppId)
      const postfixDomain = appDef?.regions?.find(e => e.name === region?.id)?.domain_postfix || appDef?.domain_postfix
      setDomainPostFix(postfixDomain)
    }
  }, [apps, contextAppId, region])

  useEffect(() => {
    if (isNonemptyArray(instances) && contextAppId) {
      const existingTenantUrl = instances?.get(contextAppId)?.url
      const subdomain = existingTenantUrl?.substring(existingTenantUrl.indexOf('https://') + 8, existingTenantUrl.lastIndexOf(domainPostFix))
      if (subdomain) {
        setSubExistingDomain(subdomain)
        setValue('subdomain', subdomain)
        setCurrentTypedInSubDomain(undefined)
        clearErrors('subdomain')
      }
    }
    else {
      setSubExistingDomain(undefined)
      setValue('subdomain', undefined)
    }
  }, [apps, domainPostFix, subExistingDomain, instances, contextAppId, clearErrors, setValue])

  return (
    <>
      <Title size='xxs' level={2} addClassName='cm-mb-[12px]'>{APP_SUBDOMAIN}</Title>
      <div className="tw-flex" style={{ flex: 2 }}>
        <Input
          disabled={Boolean(subExistingDomain) || !Boolean(dpTsgId)}
          label= {APP_SUBDOMAIN}
          value={subExistingDomain || currentTypedInSubDomain}
          showLabel={false}
          style={{ textTransform: 'lowercase' }}
          placeholder="url Subdomain"
          addClassName="tw-w-64"
          name='subdomain'
          data-testid='activation-sub-domain'
          data-metrics='activation-sub-domain'
          onInput={(e) => setCurrentTypedInSubDomain(e.currentTarget.value)}
          register={{
            validate: Validation({
              tsgId,
              globalTsgId,
              subExistingDomain,
              domainPostFix,
              appId: contextAppId,
              validationRules: { validation_required: !subExistingDomain }
            }),
          }}
        />
        <div className="tw-pl-1 tw-py-1">{domainPostFix}</div>
      </div>
    </>
  )
}

SubDomainField.propTypes = {}

export default SubDomainField
